import { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

const MEMBER_ACTIVE_GYM_KEY = 'member_active_gym';

interface MemberBranch {
  id: string;
  name: string;
  displayName: string;
}

export interface MemberGym {
  id: string;
  name: string;
  displayName: string;
  logoUrl?: string | null;
  branches: MemberBranch[];
  features: {
    class_scheduling: boolean;
    store?: boolean;
    /** A usable payment gateway is configured for this gym. */
    payments?: boolean;
    /** The gym collects for drop-in bookings online (gateway + its opt-in). */
    bookingPayments?: boolean;
  };
  /** Front-desk numbers, for bookings the desk has to take. */
  phoneNumbers?: string[];
  /** IANA zone — gym-scoped "today" must come from this, not the device clock. */
  timezone?: string;
  /** The member's lifecycle AT this gym: lead | trial | active | paused | inactive | suspended */
  memberStatus?: string;
  /** YYYY-MM-DD or null — set while a trial is running (or after it lapsed). */
  trialEndDate?: string | null;
}

export const useMemberGyms = (token: string | null) => {
  const [gyms, setGyms] = useState<MemberGym[]>([]);
  const [activeGymId, setActiveGymIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const setActiveGymId = useCallback((gymId: string | null) => {
    setActiveGymIdState(gymId);
    if (gymId) {
      localStorage.setItem(MEMBER_ACTIVE_GYM_KEY, gymId);
    } else {
      localStorage.removeItem(MEMBER_ACTIVE_GYM_KEY);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchGyms = async () => {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/app/member/gyms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setGyms(result.data);
        const stored = localStorage.getItem(MEMBER_ACTIVE_GYM_KEY);
        const fallback = result.data[0]?.id || null;
        const selected = stored && result.data.some((gym: MemberGym) => gym.id === stored) ? stored : fallback;
        setActiveGymIdState(selected);
        if (selected) {
          localStorage.setItem(MEMBER_ACTIVE_GYM_KEY, selected);
        }
      } else {
        setGyms([]);
      }
      setLoading(false);
    };

    fetchGyms();
  }, [token]);

  return { gyms, activeGymId, setActiveGymId, loading };
};

